import type { MantineColorsTuple } from '@mantine/core'

// ──────────────────────────────────────────────────────────
// HSL 変換ユーティリティ（外部ライブラリ不要）
// ──────────────────────────────────────────────────────────

/**
 * 6桁のHEXカラー文字列を正規化する（オプションの先頭 # を許可）
 * - 許可形式: "#RRGGBB" または "RRGGBB"
 * - それ以外はエラーを投げる
 */
function normalizeHex6(hex: string): string {
  const trimmed = hex.trim()
  const match = /^#?([0-9a-fA-F]{6})$/.exec(trimmed)
  if (!match) {
    throw new Error(`hexToHsl: invalid hex color "${hex}". Expected a 6-digit hex string like "#RRGGBB".`)
  }
  return match[1].toLowerCase()
}

/** HEX → { h: 0-360, s: 0-100, l: 0-100 } (浮動小数点) */
function hexToHsl(hex: string): { h: number; s: number; l: number } {
  const clean = normalizeHex6(hex)
  const r = Number.parseInt(clean.slice(0, 2), 16) / 255
  const g = Number.parseInt(clean.slice(2, 4), 16) / 255
  const b = Number.parseInt(clean.slice(4, 6), 16) / 255

  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  const l = (max + min) / 2

  if (max === min) return { h: 0, s: 0, l: l * 100 }

  const d = max - min
  const s = l < 0.5 ? d / (max + min) : d / (2 - max - min)

  let h: number
  if (max === r) {
    h = (g - b) / d + (g < b ? 6 : 0)
  } else if (max === g) {
    h = (b - r) / d + 2
  } else {
    h = (r - g) / d + 4
  }

  return {
    h: (h / 6) * 360, // 0-360 (浮動小数点)
    s: s * 100, // 0-100 (浮動小数点)
    l: l * 100, // 0-100 (浮動小数点)
  }
}

/** { h: 0-360, s: 0-100, l: 0-100 } → HEX */
function hslToHex(h: number, s: number, l: number): string {
  const hNorm = ((h % 360) + 360) % 360
  const sNorm = s / 100
  const lNorm = l / 100

  const c = (1 - Math.abs(2 * lNorm - 1)) * sNorm
  const x = c * (1 - Math.abs(((hNorm / 60) % 2) - 1))
  const m = lNorm - c / 2

  let r = 0
  let g = 0
  let b = 0

  if (hNorm < 60) {
    r = c
    g = x
    b = 0
  } else if (hNorm < 120) {
    r = x
    g = c
    b = 0
  } else if (hNorm < 180) {
    r = 0
    g = c
    b = x
  } else if (hNorm < 240) {
    r = 0
    g = x
    b = c
  } else if (hNorm < 300) {
    r = x
    g = 0
    b = c
  } else {
    r = c
    g = 0
    b = x
  }

  const toHex = (n: number) =>
    Math.round((n + m) * 255)
      .toString(16)
      .padStart(2, '0')

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`
}

// ──────────────────────────────────────────────────────────
// Figma Step 公式
//
// Primary/BaseのHSLを HBase/SBase/LBaseとする
//
// <Step一覧>
//   Hover   = Lbase × 0.88
//   Active  = Lbase × 0.78
//   Light   = Lbase + (100 - Lbase) × 0.80
//   Tint    = Lbase + (100 - Lbase) × 0.90
//   Border  = Lbase + (100 - Lbase) × 0.55
//
// ※ Mantine 10スロット中 Figma 未定義のスロット [3][4][8][9] は
//    隣接する定義済みスロット間を補間して埋める。
// ──────────────────────────────────────────────────────────

/**
 * 単一の HEX カラーから Mantine 互換の 10スロット MantineColorsTuple を生成する。
 *
 * Figma `Raund-Replace / Components / Color`（node-id: 37:1331）に定義された
 * HSL ベースのカラースケール生成ロジックに準拠。
 *
 * H・S を固定したまま L のみを調整することで、
 * 色相のブレなく明暗バリエーションを生成できる。
 *
 * スロットマッピング:
 *   [0] Tint   — L = Lbase + (100 - Lbase) × 0.90
 *   [1] Light  — L = Lbase + (100 - Lbase) × 0.80
 *   [2] Border — L = Lbase + (100 - Lbase) × 0.55
 *   [3] 補間   — L = Lbase + (100 - Lbase) × 0.30  （Figma 外: 補間）
 *   [4] 補間   — L = Lbase + (100 - Lbase) × 0.10  （Figma 外: 補間）
 *   [5] Base   — L = Lbase                           ← primaryShade = 5
 *   [6] Hover  — L = Lbase × 0.88
 *   [7] Active — L = Lbase × 0.78
 *   [8] 補間   — L = Lbase × 0.65                   （Figma 外: 補間）
 *   [9] 最暗   — L = Lbase × 0.50                   （Figma 外: 補間）
 *
 * @param baseHex - ベースカラーの HEX 文字列（例: '#FFC500'）
 */
export function generateColorScale(baseHex: string): MantineColorsTuple {
  const { h, s, l } = hexToHsl(baseHex)

  const slot = (lightness: number) => hslToHex(h, s, Math.min(100, Math.max(0, lightness)))

  return [
    slot(l + (100 - l) * 0.9), // [0] Tint
    slot(l + (100 - l) * 0.8), // [1] Light
    slot(l + (100 - l) * 0.55), // [2] Border
    slot(l + (100 - l) * 0.3), // [3] 補間
    slot(l + (100 - l) * 0.1), // [4] 補間
    baseHex, // [5] Base ← primaryShade
    slot(l * 0.88), // [6] Hover
    slot(l * 0.78), // [7] Active
    slot(l * 0.65), // [8] 補間
    slot(l * 0.5), // [9] 最暗
  ] as MantineColorsTuple
}
